import React from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import classNames from 'classnames';
import { Form } from 'formik';

import CollapsibleCard from 'core/components/collapsible-card';
import { FormField } from 'core/components/form';
import { Label } from 'core/components/label';
import RemoveButton from 'core/components/remove-button';

import useStyles from './loss-history.styles';
import { canIgnoreLossEntries, isManualEntry } from './helpers';

const CURRENCY_FORMAT = { thousandSeparator: true, prefix: '$', decimalScale: 2 };

// Field layout for one entry. `source` is always read-only (it identifies the report or the
// manual origin and must never be re-labeled by a user).
const FIELDS = [
  { name: 'causeOfLoss', label: 'Cause of Loss', type: 'string' },
  // A loss always happened in the past, so never fall back to today.
  { name: 'lossDate', label: 'Loss Date', type: 'date', defaultToEmpty: true },
  { name: 'carrierName', label: 'Carrier Name', type: 'string' },
  { name: 'claimNumber', label: 'Claim Number', type: 'string' },
  { name: 'policyNumber', label: 'Policy Number', type: 'string' },
  { name: 'policyType', label: 'Policy Type', type: 'string' },
  { name: 'claimStatus', label: 'Claim Status', type: 'string' },
  { name: 'catastropheNumber', label: 'Catastrophe Number', type: 'string' },
  { name: 'paidAmount', label: 'Paid Amount', type: 'numeric', format: CURRENCY_FORMAT },
  { name: 'lossAmount', label: 'Loss Amount', type: 'numeric', format: CURRENCY_FORMAT },
  { name: 'property', label: 'Property', type: 'string' },
  { name: 'source', label: 'Source', type: 'string', readOnly: true },
  { name: 'comments', label: 'Comments', type: 'textarea', xs: 12 },
  { name: 'lossDescription', label: 'Loss Description', type: 'textarea', xs: 12 }
];

const sourceLabel = (item) =>
  isManualEntry(item) ? `Manual Entry${item.addedBy ? ` (${item.addedBy})` : ''}` : item.source || 'Report';

// #33939 — one Loss History entry. Editability is decided by the section and threaded in via
// `disabled` (on a POLICY it's UW-only per #33957 review; on an OFFER it follows offer-edit
// permission). The Ignore control is UW-only everywhere (it changes the claim-free discount).
// Ignored entries stay visible but greyed out.
const LossHistoryItem = ({
  index,
  fieldName,
  item,
  removeFromList,
  onRemove,
  initialFold,
  onFold,
  disabled,
  isUnderwriter
}) => {
  const classes = useStyles();
  const editable = !disabled;
  // Ignore is additionally UW-gated (FormField only auto-disables off the global DisableContext,
  // so this must be explicit).
  const showIgnore = !disabled && canIgnoreLossEntries({ isUnderwriter });

  return (
    <CollapsibleCard
      className={classNames({ [classes.ignoredCard]: item.ignore })}
      content={
        <Label type="infoSubtitle" className={classes.itemHeader}>
          {`${item.causeOfLoss || 'Loss'} — ${item.lossDate || 'No date'} · ${sourceLabel(item)}`}
          {item.ignore ? ' (Ignored)' : ''}
        </Label>
      }
      contentRight={editable && <RemoveButton mode="big" onClick={() => onRemove(index, removeFromList)} />}
      initialOpen={initialFold}
      onChange={(val) => onFold(index, val)}
    >
      <Form disabled={disabled} className={classes.itemForm}>
        <Grid container justifyContent="space-around" alignItems="center" spacing={4}>
          {FIELDS.map((field) =>
            editable && !field.readOnly ? (
              <FormField
                key={field.name}
                mode="light"
                type={field.type}
                name={`${fieldName}.${field.name}`}
                id={`${fieldName}.${field.name}`}
                label={field.label}
                format={field.format}
                xs={field.xs || 6}
                defaultToEmpty={field.defaultToEmpty}
              />
            ) : (
              <FormField
                key={field.name}
                mode="light"
                type="value"
                id={`${fieldName}.${field.name}`}
                label={field.label}
                value={item[field.name]}
                xs={field.xs || 6}
              />
            )
          )}
          {showIgnore && (
            <FormField
              mode="light"
              type="checkbox"
              name={`${fieldName}.ignore`}
              id={`${fieldName}.ignore`}
              label="Ignore (exclude from summary & rating)"
              xs={12}
            />
          )}
        </Grid>
      </Form>
    </CollapsibleCard>
  );
};

LossHistoryItem.propTypes = {
  index: PropTypes.number.isRequired,
  fieldName: PropTypes.string.isRequired,
  item: PropTypes.object.isRequired,
  removeFromList: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  initialFold: PropTypes.bool,
  onFold: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  isUnderwriter: PropTypes.bool
};

LossHistoryItem.defaultProps = {
  initialFold: false,
  disabled: false,
  isUnderwriter: false
};

export default LossHistoryItem;
